import { useEffect, useRef } from 'react'

const BAR_COUNT = 24
const MIN_SCALE = 0.15

/**
 * Real input-level bars driven by an AnalyserNode on the mic stream: they only move
 * when the mic actually hears something ("I'm listening" feedback, not decoration).
 * Honors prefers-reduced-motion by rendering static mid-height bars.
 */
export const LiveWaveform = ({ stream }: { stream: MediaStream }) => {
  const barsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const bars = barsRef.current?.children
    if (!bars) return

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      Array.from(bars).forEach((bar) => ((bar as HTMLElement).style.transform = 'scaleY(0.4)'))
      return
    }

    const audioContext = new AudioContext()
    // iOS Safari: contexts created outside a user gesture start 'suspended' (flat bars without this).
    if (audioContext.state === 'suspended') audioContext.resume().catch(() => undefined)
    const source = audioContext.createMediaStreamSource(stream)
    const analyser = audioContext.createAnalyser()
    analyser.fftSize = 64 // 32 frequency bins, plenty for a level meter
    source.connect(analyser)
    const data = new Uint8Array(analyser.frequencyBinCount)
    let frame = 0

    const draw = () => {
      analyser.getByteFrequencyData(data)
      for (let i = 0; i < bars.length; i++) {
        // Spread the low bins (voice lives there) across the bars, mirrored from the center.
        const bin = Math.abs(i - bars.length / 2)
        const level = (data[Math.min(bin, data.length - 1)] ?? 0) / 255
        const scale = MIN_SCALE + level * (1 - MIN_SCALE)
        ;(bars[i] as HTMLElement).style.transform = `scaleY(${scale.toFixed(3)})`
      }
      frame = requestAnimationFrame(draw)
    }
    frame = requestAnimationFrame(draw)

    return () => {
      cancelAnimationFrame(frame)
      source.disconnect()
      audioContext.close().catch(() => undefined)
    }
  }, [stream])

  return (
    <div ref={barsRef} aria-hidden className="flex h-6 min-w-0 flex-1 items-center justify-center gap-[4px]">
      {Array.from({ length: BAR_COUNT }, (_, i) => (
        // Initial scale matches MIN_SCALE: without it the bars render one frame at full
        // height before the first RAF tick, flashing like a layout jump.
        <span
          key={i}
          className="h-full w-[2px] shrink rounded-full bg-primary/70 transition-transform duration-75 will-change-transform"
          style={{ transform: `scaleY(${MIN_SCALE})` }}
        />
      ))}
    </div>
  )
}
